import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const PublicLayout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileExpandedMenu, setMobileExpandedMenu] = useState(null);
  const dropdownRef = useRef(null);
  const location = useLocation();

  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu and scroll to top when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileExpandedMenu(null);
    setActiveDropdown(null);
    // Scroll to top of page on route change
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  // Navigation structure with 6 main tabs
  const navigationItems = [
    {
      id: 'home',
      label: 'Home',
      to: '/',
      submenu: null
    },
    {
      id: 'about-samaj',
      label: 'About Samaj',
      submenu: [
        { to: '/hinglaj-kuldevi', label: 'Hinglaj the Kuldevi' },
        { to: '/upatti-origin', label: 'Utpatti' },
        { to: '/branches-and-nukhs', label: 'Gotras and Nukhs' },
        { to: '/presence-of-samaj', label: 'Presence' },
        { to: '/organisations', label: 'Local Samaj Darshan' },
        { to: '/temples', label: 'Hinglaj Temples' },
        { to: '/hostels', label: 'Hostels' },
        { to: '/dharamshalas', label: 'Dharamshalas' },
        { to: '/social-welfare-activities', label: 'Social Welfare Activities' },
      ]
    },
    {
      id: 'mahasabha',
      label: 'About Mahasabha',
      submenu: [
        { to: '/objects-and-functions', label: 'Objects and Functions' },
        { to: '/history', label: 'History' },
        { to: '/governing-body', label: 'Governing Body' },
        { to: '/members', label: 'Members' },
        { to: '/scholarship', label: 'Scholarship' },
        { to: '/vision-convention-2026', label: 'Vision Convention 2026' },
      ]
    },
    {
      id: 'families-data',
      label: 'Families Data',
      submenu: [
        { to: '/families-register', label: 'Digital Register' },
        { to: '/matrimonial', label: 'Matrimonial' },
        { to: '/auth?tab=family', label: 'Family Login' },
      ]
    },
    {
      id: 'events-news',
      label: 'Events and News',
      submenu: [
        { to: '/events', label: 'Events' },
        { to: '/programs-and-news', label: 'News' },
        { to: '/gallery', label: 'Gallery' },
      ]
    }
  ];

  // Footer quick links
  const footerLinks = [
    { to: '/', label: 'Home' },
    { to: '/hinglaj-kuldevi', label: 'About Samaj' },
    { to: '/governing-body', label: 'Mahasabha' },
    { to: '/members', label: 'Members' },
    { to: '/scholarship', label: 'Scholarship' },
    { to: '/events', label: 'Events' },
  ];

  const isActive = (path) => location.pathname === path;
  
  const isMenuActive = (item) => {
    if (item.to) return location.pathname === item.to;
    if (item.submenu) {
      return item.submenu.some(sub => location.pathname === sub.to);
    }
    return false;
  };

  return (
    <div className="min-h-screen flex flex-col bg-background overflow-x-hidden">
      {/* Top Bar */}
      <div className="bg-secondary text-white py-2 text-sm hidden md:block">
        <div className="w-full px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-6">
            <a href="tel:+919414134392" className="flex items-center gap-2 hover:text-primary transition-colors">
              <Phone size={14} />
              <span>+91 9414134392</span>
            </a>
            <a href="mailto:brahmkhatrimahasabha@gmail.com" className="flex items-center gap-2 hover:text-primary transition-colors">
              <Mail size={14} />
              <span>brahmkhatrimahasabha@gmail.com</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-serif italic text-primary-200">श्री हिंगुलायै नमः</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className={`sticky top-0 z-50 bg-white transition-all duration-300 ${scrolled ? 'shadow-nav' : 'border-b border-gray-100'}`}>
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            {/* Logo - Left aligned */}
            <Link to="/" className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0 flex-1 lg:flex-none min-w-0">
              <img 
                src="/logo.webp" 
                alt="Khatri Mahasabha Logo" 
                className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0"
              />
              <div className="min-w-0">
                <h1 className="font-bold text-xs sm:text-sm text-secondary leading-tight line-clamp-2 xl:whitespace-nowrap">ब्रह्मक्षत्रिय मारवाड़ी खत्री एसोसिएशन (महासभा)</h1>
              </div>
            </Link>

            {/* Desktop Navigation - Evenly distributed across full width */}
            <nav className="hidden lg:flex items-center flex-1 justify-around ml-8" ref={dropdownRef}>
              {navigationItems.map((item) => (
                <div
                  key={item.id}
                  className="relative group"
                  onMouseEnter={() => item.submenu && setActiveDropdown(item.id)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  {item.submenu ? (
                    <>
                      <button
                        className={`px-5 py-2 text-[15px] font-semibold transition-colors relative flex items-center gap-1.5 whitespace-nowrap ${
                          isMenuActive(item)
                            ? 'text-primary nav-active'
                            : 'text-gray-700 hover:text-primary'
                        }`}
                        onClick={() => setActiveDropdown(activeDropdown === item.id ? null : item.id)}
                      >
                        {item.label}
                        <ChevronDown 
                          size={16} 
                          className={`transition-transform duration-200 ${activeDropdown === item.id ? 'rotate-180' : ''}`} 
                        />
                      </button>

                      {/* Dropdown Menu - No gap between trigger and dropdown */}
                      {activeDropdown === item.id && (
                        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50">
                          {/* Invisible bridge to prevent hover gap issue */}
                          <div className="absolute -top-2 left-0 right-0 h-2"></div>
                          <div className="w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 animate-slide-down">
                            {item.submenu.map((subItem, idx) => (
                              <Link
                                key={idx}
                                to={subItem.to}
                                className={`block px-4 py-2.5 text-sm transition-colors ${
                                  isActive(subItem.to)
                                    ? 'bg-primary/10 text-primary font-medium'
                                    : 'text-gray-700 hover:bg-gray-50 hover:text-primary'
                                }`}
                                onClick={() => setActiveDropdown(null)}
                              >
                                {subItem.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.to}
                      className={`px-5 py-2 text-[15px] font-semibold transition-colors relative whitespace-nowrap ${
                        isActive(item.to)
                          ? 'text-primary nav-active'
                          : 'text-gray-700 hover:text-primary'
                      }`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors flex-shrink-0 ml-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="lg:hidden py-4 border-t border-gray-100 animate-slide-down max-h-[70vh] overflow-y-auto" data-testid="mobile-menu">
              {navigationItems.map((item) => (
                <div key={item.id} className="mx-2">
                  {item.submenu ? (
                    <>
                      <button
                        onClick={() => setMobileExpandedMenu(mobileExpandedMenu === item.id ? null : item.id)}
                        className={`w-full flex items-center justify-between px-4 py-3 font-medium transition-colors rounded-lg ${
                          isMenuActive(item)
                            ? 'bg-primary/10 text-primary'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span>{item.label}</span>
                        <ChevronRight 
                          size={18} 
                          className={`transition-transform duration-200 ${mobileExpandedMenu === item.id ? 'rotate-90' : ''}`}
                        />
                      </button>

                      {/* Mobile Submenu */}
                      {mobileExpandedMenu === item.id && (
                        <div className="ml-4 mt-1 border-l-2 border-primary/20">
                          {item.submenu.map((subItem, idx) => (
                            <Link
                              key={idx}
                              to={subItem.to}
                              className={`block px-4 py-2.5 text-sm font-medium transition-colors rounded-r-lg ${
                                isActive(subItem.to)
                                  ? 'bg-primary/10 text-primary'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-primary'
                              }`}
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.to}
                      className={`block px-4 py-3 font-medium transition-colors rounded-lg ${
                        isActive(item.to)
                          ? 'bg-primary/10 text-primary'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </nav>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-secondary text-white">
        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* About Section */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="/logo.webp" 
                  alt="Khatri Mahasabha Logo" 
                  className="w-14 h-14 object-contain bg-white rounded-lg p-1"
                />
                <div>
                  <h3 className="font-bold text-lg">ब्रह्मक्षत्रिय मारवाड़ी खत्री एसोसिएशन (महासभा)</h3>
                </div>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed mb-4 max-w-md">
                A digital platform for our community, fostering unity, transparency, and growth. 
                Serving the Brahmkshatriya Marwadi community across India.
              </p>
              {/* Social Media */}
              <div className="flex items-center gap-3">
                <a 
                  href="https://www.youtube.com/@ParichaySangam2025" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-primary transition-colors"
                  title="YouTube"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold text-lg mb-4 text-white">Quick Links</h4>
              <ul className="space-y-2">
                {footerLinks.map((link) => (
                  <li key={link.to + link.label}>
                    <Link 
                      to={link.to} 
                      className="text-gray-300 hover:text-primary text-sm transition-colors flex items-center gap-2"
                    >
                      <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-semibold text-lg mb-4 text-white">Contact Us</h4>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-primary-200 font-medium mb-1">Headquarters</p>
                  <p className="text-gray-300 leading-relaxed">
                    15/287, Chopasani Housing Board,<br />
                    Jodhpur, Rajasthan - 342008
                  </p>
                </div>
                <div>
                  <p className="text-primary-200 font-medium mb-1">Phone</p>
                  <a href="tel:+919414134392" className="text-gray-300 hover:text-primary transition-colors">
                    +91 9414134392
                  </a>
                </div>
                <div>
                  <p className="text-primary-200 font-medium mb-1">Email</p>
                  <a href="mailto:brahmkhatrimahasabha@gmail.com" className="text-gray-300 hover:text-primary transition-colors break-all">
                    brahmkhatrimahasabha@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <p className="text-xs text-gray-400 text-center leading-relaxed">
              <strong>Disclaimer:</strong> ब्रह्मक्षत्रिय मारवाड़ी खत्री समाज की धरोहर से संबंधित जानकारी समाजबंधुओं के सहयोग से संकलित की गई है। इसका विवरण वेबसाइट पर उसी आधार पर प्रस्तुत किया गया है। कुछ जानकारी इंटरनेट एवं सार्वजनिक डोमेन में उपलब्ध स्रोतों से भी ली गई है। यदि किसी तथ्य में सुधार या अतिरिक्त जानकारी हो, तो समाज के हित में उसका स्वागत है।
            </p>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
              <p>&copy; {new Date().getFullYear()} ब्रह्मक्षत्रिय मारवाड़ी खत्री एसोसिएशन (महासभा). All rights reserved.</p>
              <div className="flex items-center gap-4">
                <Link to="/byelaws" className="hover:text-primary transition-colors">Terms of Use</Link>
                <span>|</span>
                <Link to="/contact" className="hover:text-primary transition-colors">Privacy Policy</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
